import { ReactNode } from 'react';
import { Stack, Typography } from '@mui/material';

import { EMlpIconTypes, EMplVariantTypes, MplIconMessage } from '@mopla/ui';

interface ISubscriptionEditOptionProps {
	title: ReactNode;
	description: ReactNode;
	warningMessage?: ReactNode;
	infoMessage?: ReactNode;
	children: ReactNode;
	/**
	 * Per-action test-id prefix (e.g. "dticket-subscriptionEdit-cancel"). Both
	 * edit options render this same component, so the title / description / info
	 * blocks — which carry the cancellation & pause dates — need an action-scoped
	 * id to stay unique and targetable (AQA).
	 */
	testIdPrefix?: string;
}

export const SubscriptionEditOption = ({
	title,
	description,
	warningMessage,
	infoMessage,
	children,
	testIdPrefix,
}: ISubscriptionEditOptionProps) => {
	return (
		<Stack gap={2}>
			<Typography
				variant="h4-sb"
				color="primary.main"
				data-testid={testIdPrefix ? `${testIdPrefix}-title` : undefined}
			>
				{title}
			</Typography>
			<Typography
				variant="b2"
				color="primary.main"
				data-testid={testIdPrefix ? `${testIdPrefix}-description` : undefined}
			>
				{description}
			</Typography>
			{warningMessage && (
				<MplIconMessage
					type={EMlpIconTypes.Warning}
					variant={EMplVariantTypes.Warning}
					message={warningMessage}
					dataTestid={
						testIdPrefix
							? `${testIdPrefix}-warning-iconMessage`
							: 'dticket-subscriptionEditOption-warning-iconMessage'
					}
				/>
			)}
			{children}
			{infoMessage && (
				<MplIconMessage
					type={EMlpIconTypes.Information}
					variant={EMplVariantTypes.Default}
					message={infoMessage}
					dataTestid={
						testIdPrefix
							? `${testIdPrefix}-info-iconMessage`
							: 'dticket-subscriptionEditOption-info-iconMessage'
					}
				/>
			)}
		</Stack>
	);
};
